import type Phaser from "phaser";

/**
 * 9-slice border 픽셀 값 — NineSlice texture의 모서리 영역.
 * 일반적으로 atlas asset_manifest의 slice 값 그대로 1:1 매칭.
 */
export interface NineSliceBorder {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

type TextStyle = Phaser.Types.GameObjects.Text.TextStyle;

/** `NineSliceButton`에 전달되는 스타일 토큰 — 호출자가 도메인 `BUTTON_STYLES`에서 변형(primary/diceNext 등) 선택. */
export interface ButtonStyleToken {
  readonly textureKey: string;
  readonly border: NineSliceBorder;
  readonly width: number;
  readonly height: number;
  readonly textStyle: TextStyle;
  /** hover/active alpha 변형 (부분 갱신용). default 1.0. */
  readonly alphaIdle?: number;
  readonly alphaActive?: number;
  /**
   * 비활성(setEnabled(false)) 상태 클릭 시 거부 피드백(playDenyFx) 틴트 색 — opt-in.
   * 미지정이면 비활성 클릭은 기존처럼 무음 no-op(click 미발화만). 색은 호출자(테마) 소유.
   */
  readonly denyTint?: number;
}

/** `NineSlicePanel`에 전달되는 스타일 토큰. */
export interface PanelStyleToken {
  readonly textureKey: string;
  readonly border: NineSliceBorder;
  /** 패널 내부 컨텐츠가 들어갈 여유 padding (호출자가 layout 계산에 사용). */
  readonly contentPadding: number;
}

/** `NineSliceCard`에 전달되는 스타일 토큰 — normal/selected 텍스처 + alpha 차이로 강조. */
export interface CardStyleToken {
  readonly normalKey: string;
  readonly selectedKey: string;
  readonly border: NineSliceBorder;
  readonly alphaNormal: number;
  readonly alphaSelected: number;
  readonly noteTextStyle: TextStyle;
}

/**
 * `SegmentedTabBar`에 전달되는 스타일 토큰 — 활성/비활성 NineSlice 텍스처 + 라벨 스타일.
 * 활성 탭은 `activeTextureKey` + `labelActiveStyle`(미지정 시 `labelStyle`) + `activeLabelScale` 확대,
 * 비활성 탭은 `inactiveTextureKey` + `labelStyle` + `inactiveAlpha`. 에셋키/레이아웃은 호출자 책임(DIP).
 */
export interface TabBarStyleToken {
  readonly activeTextureKey: string;
  /** 비활성 탭 배경 텍스처 — 활성과 동일 키여도 됨(알파만 차이). */
  readonly inactiveTextureKey: string;
  readonly border: NineSliceBorder;
  readonly labelStyle: TextStyle;
  /** 활성 탭 라벨 스타일 — 미지정 시 `labelStyle` 사용. */
  readonly labelActiveStyle?: TextStyle;
  /** 비활성 탭 배경 알파. default 0.8. */
  readonly inactiveAlpha?: number;
  /** 활성 탭 라벨 scale. default 1.04. */
  readonly activeLabelScale?: number;
}

/**
 * `NineSliceButton` press on/off scale tween 타이밍. constructor `pressTiming` option으로 override 가능.
 * default: press on 80ms Sine.easeOut, press off 140ms Back.easeOut(살짝 튕김) — tactile feedback.
 */
export interface ButtonPressTiming {
  readonly pressedScale: number;
  readonly pressOnDurationMs: number;
  readonly pressOnEase: string;
  readonly pressOffDurationMs: number;
  readonly pressOffEase: string;
}

export const DEFAULT_BUTTON_PRESS_TIMING: ButtonPressTiming = {
  pressedScale: 0.92,
  pressOnDurationMs: 80,
  pressOnEase: "Sine.easeOut",
  pressOffDurationMs: 140,
  pressOffEase: "Back.easeOut",
};

/**
 * `ModalLayer` 등장/퇴장 tween 타이밍. constructor `modalTiming` option으로 override.
 * default: enter 180ms Back.easeOut scale 0.9→1, exit 140ms Sine.easeIn scale 1→0.94.
 * Container scale은 origin(0,0) 기준이라 자식 절대좌표가 좌상단으로 쏠림 — ModalLayer가 screenWidth/Height 기반 pivot 보정 자동 적용.
 */
export interface ModalTiming {
  readonly enterDurationMs: number;
  readonly enterEase: string;
  readonly enterFromScale: number;
  readonly exitDurationMs: number;
  readonly exitEase: string;
  readonly exitToScale: number;
}

export const DEFAULT_MODAL_TIMING: ModalTiming = {
  enterDurationMs: 180,
  enterEase: "Back.easeOut",
  enterFromScale: 0.9,
  exitDurationMs: 140,
  exitEase: "Sine.easeIn",
  exitToScale: 0.94,
};
